import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Heart, Pill, FileText, Calendar, 
  Activity, Utensils, Dumbbell, TrendingUp,
  RefreshCw, Bell, Leaf, User, Droplet, Edit2
} from 'lucide-react';
import HealthTrends from '../components/HealthTrends';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FamilyMemberDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const memberId = searchParams.get('id');
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [member, setMember] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentEntries, setRecentEntries] = useState([]);

  useEffect(() => {
    if (memberId) {
      fetchMemberData();
    }
  }, [memberId]);

  const fetchMemberData = async () => {
    try {
      const token = localStorage.getItem('sehatSetu_token');
      
      // Fetch family members to get the specific member info
      const membersRes = await axios.get(`${API_URL}/family/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const foundMember = membersRes.data.members?.find(m => m.id === memberId);
      if (foundMember) {
        setMember(foundMember);
      }

      // Fetch stats for this member
      const statsRes = await axios.get(`${API_URL}/dashboard/stats?family_member_id=${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(statsRes.data);

      // Fetch recent diary entries
      const diaryRes = await axios.get(`${API_URL}/health/diary?family_member_id=${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentEntries((diaryRes.data || []).slice(0, 5));

    } catch (error) {
      console.error('Error fetching member data:', error);
      toast.error('Failed to load member data');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';
  };

  const getRelationshipLabel = (relationship) => {
    const labels = {
      self: 'Account Owner',
      grandparent: 'Grandparent',
      parent: 'Parent',
      spouse: 'Spouse',
      child: 'Child',
      sibling: 'Sibling',
      other: 'Other'
    };
    return labels[relationship] || relationship;
  };

  const quickActions = [
    { icon: Heart, label: language === 'hi' ? 'चिकित्सा इतिहास' : 'Medical History', path: `/medical-history?member=${memberId}`, color: 'bg-red-100 text-red-600' },
    { icon: Pill, label: language === 'hi' ? 'नुस्खे' : 'Prescriptions', path: `/prescriptions?member=${memberId}`, color: 'bg-blue-100 text-blue-600' },
    { icon: Leaf, label: language === 'hi' ? 'सप्लीमेंट्स' : 'Supplements', path: `/supplements?member=${memberId}`, color: 'bg-green-100 text-green-600' },
    { icon: FileText, label: language === 'hi' ? 'रिपोर्ट्स' : 'Reports', path: `/reports?member=${memberId}`, color: 'bg-teal-100 text-teal-600' },
    { icon: Calendar, label: language === 'hi' ? 'डायरी' : 'Diary', path: `/diary?member=${memberId}`, color: 'bg-purple-100 text-purple-600' },
    { icon: Bell, label: language === 'hi' ? 'रिमाइंडर' : 'Reminders', path: `/reminders?member=${memberId}`, color: 'bg-amber-100 text-amber-600' },
  ];

  const diaryQuickActions = [
    { icon: Activity, label: language === 'hi' ? 'ब्लड प्रेशर' : 'Blood Pressure', type: 'blood_pressure', color: 'bg-red-50 text-red-500' },
    { icon: Utensils, label: language === 'hi' ? 'भोजन' : 'Food Diary', type: 'food', color: 'bg-yellow-50 text-yellow-600' },
    { icon: Dumbbell, label: language === 'hi' ? 'व्यायाम' : 'Exercise', type: 'exercise', color: 'bg-green-50 text-green-600' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-[#0D2340]" />
      </div>
    );
  }

  if (!member) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="text-center">
          <User className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Member not found</h2>
          <Button onClick={() => navigate('/family')} className="mt-4">
            Go to Family
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0D2340] to-[#0B7EA3] text-white px-4 sm:px-6 lg:px-8 py-6">
        <div className="w-full">
          <button 
            onClick={() => navigate('/family')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
            data-testid="back-to-family"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Family</span>
          </button>
          
          <div className="flex items-center gap-4">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0"
              style={{ backgroundColor: member.avatar_color || '#FFB300' }}
            >
              {member.is_self ? <User className="h-8 w-8" /> : getInitials(member.name)}
            </div>
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold font-['Manrope']">
                {member.is_self ? 'My Health' : `${member.name}'s Health`}
              </h1>
              <div className="flex items-center gap-3 mt-1 text-white/70">
                <span>{getRelationshipLabel(member.relationship)}</span>
                {member.blood_group && (
                  <>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Droplet className="h-4 w-4" />
                      {member.blood_group}
                    </span>
                  </>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              className="text-white hover:bg-white/20"
              onClick={() => navigate(`/family?edit=${member.id}`)}
              data-testid="edit-member-profile"
            >
              <Edit2 className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 py-6 space-y-6 pb-24">
        {/* Stats Overview */}
        <Card className="bg-gradient-to-r from-[#0D2340] to-[#0B7EA3] text-white" data-testid="member-vitals-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']">
              <Activity className="h-5 w-5" />
              {language === 'hi' ? 'स्वास्थ्य सारांश' : 'Health Overview'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatBox label={language === 'hi' ? 'चिकित्सा इतिहास' : 'Medical History'} value={stats?.medical_history_count || 0} icon={Heart} />
              <StatBox label={language === 'hi' ? 'नुस्खे' : 'Prescriptions'} value={stats?.prescriptions_count || 0} icon={Pill} />
              <StatBox label={language === 'hi' ? 'रिपोर्ट्स' : 'Reports'} value={stats?.reports_count || 0} icon={FileText} />
              <StatBox label={language === 'hi' ? 'डायरी एंट्री' : 'Diary Entries'} value={stats?.diary_count || 0} icon={Calendar} />
            </div>
            {stats?.recent_vitals?.blood_pressure && (
              <div className="mt-4 p-3 bg-white/10 rounded-lg">
                <p className="text-sm opacity-80">{language === 'hi' ? 'नवीनतम BP' : 'Latest BP'}</p>
                <p className="text-2xl font-bold font-['Manrope']">
                  {stats.recent_vitals.blood_pressure.systolic}/{stats.recent_vitals.blood_pressure.diastolic} mmHg
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div>
          <h2 className="text-lg font-semibold text-[#0D2340] mb-3 font-['Manrope']">
            {language === 'hi' ? 'स्वास्थ्य रिकॉर्ड' : 'Health Records'}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {quickActions.map((action, index) => (
              <Card
                key={index}
                className="card-interactive cursor-pointer"
                onClick={() => navigate(action.path)}
                data-testid={`member-action-${action.path.split('/')[1]}`}
              >
                <CardContent className="p-4 flex flex-col items-center text-center gap-2">
                  <div className={`p-3 rounded-xl ${action.color}`}>
                    <action.icon className="h-6 w-6" />
                  </div>
                  <p className="font-medium text-sm font-['Manrope']">{action.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Health Trends */}
        <div>
          <h2 className="text-lg font-semibold text-[#0D2340] mb-3 font-['Manrope']">
            {language === 'hi' ? '📊 स्वास्थ्य रुझान' : '📊 Health Trends'}
          </h2>
          <HealthTrends familyMemberId={memberId} />
        </div>

        {/* Diary Quick Actions */}
        <Card data-testid="member-diary-quick-actions">
          <CardHeader>
            <CardTitle className="font-['Manrope']">
              {language === 'hi' ? 'डायरी - त्वरित कार्रवाई' : 'Diary - Quick Actions'}
            </CardTitle>
            <CardDescription>
              {language === 'hi' ? 'नई एंट्री जोड़ें' : 'Add new entries'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3">
              {diaryQuickActions.map((action, index) => (
                <Button
                  key={index}
                  variant="outline"
                  className={`h-20 flex-col gap-2 ${action.color} border-transparent hover:border-[#FFB300]`}
                  onClick={() => navigate(`/diary?type=${action.type}&member=${memberId}`)}
                  data-testid={`member-diary-action-${action.type}`}
                >
                  <action.icon className="h-6 w-6" />
                  <span className="text-xs">{action.label}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        {recentEntries.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="font-['Manrope']">
                {language === 'hi' ? 'हाल की गतिविधि' : 'Recent Activity'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentEntries.map((entry, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-[#0D2340]/10 flex items-center justify-center">
                      {entry.entry_type === 'blood_pressure' && <Activity className="h-5 w-5 text-red-500" />}
                      {entry.entry_type === 'blood_sugar' && <Droplet className="h-5 w-5 text-purple-500" />}
                      {entry.entry_type === 'weight' && <TrendingUp className="h-5 w-5 text-blue-500" />}
                      {entry.entry_type === 'food' && <Utensils className="h-5 w-5 text-yellow-500" />}
                      {entry.entry_type === 'exercise' && <Dumbbell className="h-5 w-5 text-green-500" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm capitalize">{entry.entry_type?.replace('_', ' ')}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(entry.entry_date).toLocaleDateString()}
                      </p>
                    </div>
                    {entry.data && (
                      <Badge variant="outline" className="text-xs">
                        {entry.entry_type === 'blood_pressure' && `${entry.data.systolic}/${entry.data.diastolic}`}
                        {entry.entry_type === 'blood_sugar' && `${entry.data.level} mg/dL`}
                        {entry.entry_type === 'weight' && `${entry.data.value} kg`}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

const StatBox = ({ label, value, icon: Icon }) => (
  <div className="text-center">
    <Icon className="h-5 w-5 mx-auto mb-1 opacity-70" />
    <p className="text-2xl font-bold font-['Manrope']">{value}</p>
    <p className="text-xs opacity-80">{label}</p>
  </div>
);

export default FamilyMemberDashboard;
